import type { IPlaybackHeartbeatBody } from '@lucimark/shared/models/playback-heartbeat'
import z from 'zod'

/** localStorage: previous session ended gracefully (`true` / cleared on read). */
export const PLAYER_CLEAN_SHUTDOWN_STORAGE_KEY = 'lucimark.playerCleanShutdown'

/** localStorage: set after first boot telemetry read (distinguishes first install from unclean restart). */
export const PLAYER_BOOT_SESSION_MARKER_KEY = 'lucimark.playerBootSessionSeen'

export const PLAYER_BOOT_KINDS = ['cold_boot', 'app_restart'] as const
export type TPlayerBootKind = (typeof PLAYER_BOOT_KINDS)[number]

export interface TPlayerBootTelemetrySnapshot {
  lastBootAt: Date
  lastBootWasCleanShutdown: boolean | null
  lastBootHadBatteryOrPowerEvent: boolean | null
  lastBootKind: TPlayerBootKind | null
}

export const playerBootKindSchema = z.enum(PLAYER_BOOT_KINDS)

export const playerBootTelemetryHeartbeatFieldsSchema = z.object({
  /** ISO 8601 process boot time; sent once per boot on early heartbeats. */
  lastBootAt: z.string().min(1).max(64),
  lastBootWasCleanShutdown: z.boolean().optional(),
  lastBootHadBatteryOrPowerEvent: z.boolean().optional(),
  lastBootKind: playerBootKindSchema.optional()
}).strict()

export type TPlayerBootTelemetryHeartbeatFields = z.infer<typeof playerBootTelemetryHeartbeatFieldsSchema>

export function parsePlayerBootTelemetryHeartbeatFields(
  fields: TPlayerBootTelemetryHeartbeatFields
): TPlayerBootTelemetrySnapshot | null {
  const parsed = new Date(fields.lastBootAt)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return {
    lastBootAt: parsed,
    lastBootWasCleanShutdown: fields.lastBootWasCleanShutdown ?? null,
    lastBootHadBatteryOrPowerEvent: fields.lastBootHadBatteryOrPowerEvent ?? null,
    lastBootKind: fields.lastBootKind ?? null
  }
}

export function buildPlayerBootTelemetryHeartbeatFields(
  telemetry: TPlayerBootTelemetrySnapshot
): TPlayerBootTelemetryHeartbeatFields {
  const out: TPlayerBootTelemetryHeartbeatFields = {
    lastBootAt: telemetry.lastBootAt.toISOString()
  }
  if (telemetry.lastBootWasCleanShutdown !== null) {
    out.lastBootWasCleanShutdown = telemetry.lastBootWasCleanShutdown
  }
  if (telemetry.lastBootHadBatteryOrPowerEvent !== null) {
    out.lastBootHadBatteryOrPowerEvent = telemetry.lastBootHadBatteryOrPowerEvent
  }
  if (telemetry.lastBootKind != null) {
    out.lastBootKind = telemetry.lastBootKind
  }
  return out
}

export type TPlayerBootTelemetryHeartbeatBody = Pick<
  IPlaybackHeartbeatBody,
  'lastBootAt' | 'lastBootWasCleanShutdown' | 'lastBootHadBatteryOrPowerEvent' | 'lastBootKind'
>

export function buildPlayerBootTelemetryHeartbeatBody(
  telemetry: TPlayerBootTelemetrySnapshot
): TPlayerBootTelemetryHeartbeatBody {
  return buildPlayerBootTelemetryHeartbeatFields(telemetry)
}
